/**
 * @file   src\components\AgentWantAd\BuyerRequestedConcessionsInput.js
 * @brief  Input field for buyer requested concessions (rate buy down, closing costs, etc.)
 * @date   January, 2026
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect, useRef } from "react";
import { handleTextBoxChange } from "../../actions/common";
import { capitalizeFirstString, trimString } from "../../helpers/common";
import TextArea from "../Common/TextArea";
import { ERROR_TEXT } from "../../constants/common";

const BuyerRequestedConcessionsInput = ({
  handleFormValues,
  otherConcessions = "",
  errors = [],
}) => {
  const [otherConcessionError, setOtherConcessionError] = useState(false);
  const [descCursorPos, setDescCursorPos] = useState(0);
  const [descCharCount, setDescCharCount] = useState(0);
  const [description, setDescription] = useState("");
  const OtherConcessionRef = useRef();

  useEffect(() => {
    setDescription(otherConcessions);
    setDescCharCount(otherConcessions?.length || 0);
  }, [otherConcessions]);

  useEffect(() => {
    if (OtherConcessionRef.current) {
      OtherConcessionRef.current.setSelectionRange(descCursorPos, descCursorPos);
    }
  }, [descCursorPos]);

  // Show error message
  useEffect(() => {
    if (errors?.length > 0) {
      if (errors[0] === "otherConcession" && OtherConcessionRef.current) {
        OtherConcessionRef.current.focus();
      }
      if (errors.includes("otherConcession")) {
        setOtherConcessionError(ERROR_TEXT);
      }
    }
  }, [errors]);

  const sentenceCase = (input) => {
    setOtherConcessionError("");
    if (!input) return "";

    return input
      .toString()
      .replace(/(^|\. *)([a-z])/g, (match, separator, char) => {
        return separator + char.toUpperCase();
      });
  };

  const handleConcessionsChange = (e) => {
    let value = capitalizeFirstString(handleTextBoxChange(e));
    value = sentenceCase(value);
    setDescCharCount(value.length);
    setDescription(value);
    setDescCursorPos(e.target.selectionStart);

    handleFormValues({
      otherConcessions: trimString(value) !== "" ? value : ""
    });
  };

  return (
    <div className="wantad-timing-wrap pb-0">
      <div className="wantad-des-wrap select-type-wrap">
        <div className="position-relative">
          <TextArea
            className={
              otherConcessionError
                ? "form-control txt-area red-marked-formcontrol"
                : "form-control txt-area"
            }
            errorMessage={otherConcessionError}
            value={description}
            maxLength={500}
            placeholder="Optional: Rate buy down, closing costs, etc."
            onChange={handleConcessionsChange}
            txtRef={OtherConcessionRef}
            onBlur={() => handleFormValues({ focusOut: true })}
          />

          <div
            className={
              otherConcessionError ? "des-count red-marked-count" : "des-count"
            }
          >
            <span>{descCharCount}</span>/<span>500</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyerRequestedConcessionsInput;
